import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboard, cuentas, movimientos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { exportDashboardToPDF } from '../lib/pdfExport';
import { toast } from 'sonner';
import { OnboardingChecklist } from '../components/onboarding';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Percent, 
  ArrowUpRight, 
  ArrowDownRight,
  BarChart3,
  PieChart,
  Wallet,
  FileDown,
  Calendar
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Formato compacto para móvil (ej: $1.2M, $450K)
const formatCurrencyCompact = (value) => {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatPercent = (value) => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const KPICard = ({ title, value, change, icon: Icon, trend, subtitle }) => {
  const isPositive = change >= 0;
  const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;
  
  return (
    <Card className="kpi-card animate-slideUp" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-2 sm:pt-6">
        <div className="flex items-start justify-between gap-1">
          <div className="space-y-0 min-w-0 flex-1">
            <p className="text-[10px] sm:text-sm font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-sm sm:text-2xl font-bold text-foreground truncate tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {value}
            </p>
            {change !== undefined && (
              <div className={`flex items-center gap-0.5 text-[10px] sm:text-xs ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                <TrendIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                <span className="truncate">{formatPercent(change)} vs mes ant.</span>
              </div>
            )}
            {subtitle && (
              <p className="text-[9px] sm:text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className={`p-1.5 sm:p-3 rounded-md sm:rounded-xl flex-shrink-0 ${trend === 'up' ? 'bg-emerald-100' : trend === 'down' ? 'bg-red-100' : 'bg-primary/10'}`}>
            <Icon className={`w-3.5 h-3.5 sm:w-5 sm:h-5 ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-primary'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cuentasList, setCuentasList] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState('consolidado');
  const [kpis, setKpis] = useState(null);
  const [tendencias, setTendencias] = useState([]);
  const [gastosPorCategoria, setGastosPorCategoria] = useState([]);
  
  // Date filters - Dynamic based on actual data
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [selectedMes, setSelectedMes] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [filterMode, setFilterMode] = useState('mes'); // 'mes' or 'rango'
  const [selectedPeriodo, setSelectedPeriodo] = useState('1'); // Number of months: 1, 2, 3, 4, 6, 12
  const [selectedSubPeriodo, setSelectedSubPeriodo] = useState('1'); // Which bimester/trimester/etc
  
  // Year comparison states
  const [compareMode, setCompareMode] = useState(false);
  const [compareYears, setCompareYears] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  
  // Dynamic date range from actual movements
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Period options
  const periodoOptions = [
    { value: '1', label: 'Mes' },
    { value: '2', label: 'Bimestre' },
    { value: '3', label: 'Trimestre' },
    { value: '4', label: 'Cuatrimestre' },
    { value: '6', label: 'Semestre' },
    { value: '12', label: 'Año' },
  ];

  // Sub-period options based on selected period
  const getSubPeriodoOptions = () => {
    switch (selectedPeriodo) {
      case '2': // Bimestre (6 bimestres por año)
        return [
          { value: '1', label: 'Bimestre 1 (Ene-Feb)' },
          { value: '2', label: 'Bimestre 2 (Mar-Abr)' },
          { value: '3', label: 'Bimestre 3 (May-Jun)' },
          { value: '4', label: 'Bimestre 4 (Jul-Ago)' },
          { value: '5', label: 'Bimestre 5 (Sep-Oct)' },
          { value: '6', label: 'Bimestre 6 (Nov-Dic)' },
        ];
      case '3': // Trimestre (4 trimestres por año)
        return [
          { value: '1', label: 'Trimestre 1 (Ene-Mar)' },
          { value: '2', label: 'Trimestre 2 (Abr-Jun)' },
          { value: '3', label: 'Trimestre 3 (Jul-Sep)' },
          { value: '4', label: 'Trimestre 4 (Oct-Dic)' },
        ];
      case '4': // Cuatrimestre (3 cuatrimestres por año)
        return [
          { value: '1', label: 'Cuatrimestre 1 (Ene-Abr)' },
          { value: '2', label: 'Cuatrimestre 2 (May-Ago)' },
          { value: '3', label: 'Cuatrimestre 3 (Sep-Dic)' },
        ];
      case '6': // Semestre (2 semestres por año)
        return [
          { value: '1', label: 'Semestre 1 (Ene-Jun)' },
          { value: '2', label: 'Semestre 2 (Jul-Dic)' },
        ];
      default:
        return [];
    }
  };

  // Calculate start/end months based on period and sub-period
  const getDateRangeFromPeriod = () => {
    const year = parseInt(selectedYear);
    const subPeriod = parseInt(selectedSubPeriodo);
    const period = parseInt(selectedPeriodo);
    
    if (period === 1) {
      // Single month
      return { 
        start: `${year}-${selectedMonth}-01`, 
        end: `${year}-${selectedMonth}-${new Date(year, parseInt(selectedMonth), 0).getDate()}` 
      };
    } else if (period === 12) {
      // Full year
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    } else {
      // Calculate based on sub-period
      const startMonth = (subPeriod - 1) * period + 1;
      const endMonth = subPeriod * period;
      const endDay = new Date(year, endMonth, 0).getDate();
      return { 
        start: `${year}-${String(startMonth).padStart(2, '0')}-01`, 
        end: `${year}-${String(endMonth).padStart(2, '0')}-${endDay}` 
      };
    }
  };

  // Month names in Spanish
  const monthNames = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  // Reset sub-period when period changes
  useEffect(() => {
    setSelectedSubPeriodo('1');
  }, [selectedPeriodo]);

  // Update selectedMes when year or month changes
  useEffect(() => {
    setSelectedMes(`${selectedYear}-${selectedMonth}`);
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    loadCuentas();
    loadDateRange();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [selectedCuenta, selectedMes, fechaInicio, fechaFin, filterMode, selectedPeriodo, selectedSubPeriodo, selectedYear]);

  const loadDateRange = async () => {
    try {
      const response = await movimientos.getRangoFechas();
      if (response.data.years && response.data.years.length > 0) {
        setAvailableYears(response.data.years);
        // If current year not in data, set to the most recent year with data
        if (!response.data.years.includes(selectedYear)) {
          setSelectedYear(response.data.years[0]);
        }
      }
      if (response.data.meses) {
        setAvailableMonths(response.data.meses);
      }
    } catch (error) {
      console.error('Error loading date range:', error);
    }
  };

  const loadCuentas = async () => {
    try {
      const response = await cuentas.getAll();
      setCuentasList(response.data);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const params = selectedCuenta === 'consolidado' 
        ? { consolidado: true } 
        : { cuenta_id: selectedCuenta };
      
      // Calculate date range based on period type
      const periodoMeses = parseInt(selectedPeriodo);
      const baseYear = parseInt(selectedYear);
      
      let fechaInicioCalc, fechaFinCalc;
      
      if (periodoMeses === 1) {
        // Single month - use selected month
        const baseMonth = parseInt(selectedMonth);
        const lastDay = new Date(baseYear, baseMonth, 0).getDate();
        fechaInicioCalc = `${selectedYear}-${selectedMonth}-01`;
        fechaFinCalc = `${selectedYear}-${selectedMonth}-${lastDay}`;
        params.mes = selectedMes;
      } else if (periodoMeses === 12) {
        // Full year
        fechaInicioCalc = `${selectedYear}-01-01`;
        fechaFinCalc = `${selectedYear}-12-31`;
        params.fecha_inicio = fechaInicioCalc;
        params.fecha_fin = fechaFinCalc;
      } else {
        // Bimestre, Trimestre, Cuatrimestre, Semestre - use sub-period
        const subPeriod = parseInt(selectedSubPeriodo);
        const startMonth = (subPeriod - 1) * periodoMeses + 1;
        const endMonth = subPeriod * periodoMeses;
        const endDay = new Date(baseYear, endMonth, 0).getDate();
        fechaInicioCalc = `${selectedYear}-${String(startMonth).padStart(2, '0')}-01`;
        fechaFinCalc = `${selectedYear}-${String(endMonth).padStart(2, '0')}-${endDay}`;
        params.fecha_inicio = fechaInicioCalc;
        params.fecha_fin = fechaFinCalc;
      }

      const [kpisRes, tendenciasRes, gastosRes] = await Promise.all([
        dashboard.getKpis(params),
        dashboard.getTendencias({ ...params, meses: Math.max(periodoMeses, 6), mes_base: selectedMes }),
        dashboard.getGastosPorCategoria(params)
      ]);

      setKpis(kpisRes.data);
      setTendencias(tendenciasRes.data);
      setGastosPorCategoria(gastosRes.data);
      
      // Load comparison data if in compare mode
      if (compareMode && compareYears.length > 0) {
        loadComparisonData();
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Load comparison data for multiple years
  const loadComparisonData = async () => {
    try {
      const params = selectedCuenta === 'consolidado' 
        ? { consolidado: true } 
        : { cuenta_id: selectedCuenta };
      
      const yearDataPromises = compareYears.map(async (year) => {
        const yearParams = { ...params, fecha_inicio: `${year}-01-01`, fecha_fin: `${year}-12-31` };
        const [kpisRes, tendenciasRes] = await Promise.all([
          dashboard.getKpis(yearParams),
          dashboard.getTendencias({ ...yearParams, meses: 12, mes_base: `${year}-12` })
        ]);
        return {
          year,
          kpis: kpisRes.data,
          tendencias: tendenciasRes.data
        };
      });
      
      const results = await Promise.all(yearDataPromises);
      setComparisonData(results);
    } catch (error) {
      console.error('Error loading comparison data:', error);
    }
  };
  
  // Toggle year in comparison
  const toggleCompareYear = (year) => {
    setCompareYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year].sort().reverse()
    );
  };
  
  // Effect to reload comparison when years change
  useEffect(() => {
    if (compareMode && compareYears.length > 0) {
      loadComparisonData();
    }
  }, [compareYears, compareMode, selectedCuenta]);

  const COLORS = ['#BE123C', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#6366F1'];

  if (loading) {
    return (
      <div className="p-8">
        <div className="kpi-grid mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-32 rounded-xl"></div>
          ))}
        </div>
        <div className="charts-grid">
          <div className="skeleton h-80 rounded-xl"></div>
          <div className="skeleton h-80 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>
              Dashboard Financiero
            </h1>
            <p className="hidden sm:block">
              Bienvenido, {user?.nombre}. Aquí está el resumen de tu empresa.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Year Selector */}
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-20" data-testid="year-selector">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.length > 0 ? (
                  availableYears.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))
                ) : (
                  // Fallback: show current year and previous years
                  Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() - i)).map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            
            {/* Period Selector */}
            <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
              <SelectTrigger className="w-32" data-testid="periodo-selector">
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                {periodoOptions.map((periodo) => (
                  <SelectItem key={periodo.value} value={periodo.value}>
                    {periodo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Month Selector - ONLY show when "Mes" is selected */}
            {selectedPeriodo === '1' && (
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-32" data-testid="month-selector">
                  <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Sub-Period Selector - Show for Bimestre, Trimestre, Cuatrimestre, Semestre */}
            {['2', '3', '4', '6'].includes(selectedPeriodo) && (
              <Select value={selectedSubPeriodo} onValueChange={setSelectedSubPeriodo}>
                <SelectTrigger className="w-44" data-testid="subperiodo-selector">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {getSubPeriodoOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Cuenta Selector */}
            <Select value={selectedCuenta} onValueChange={setSelectedCuenta}>
              <SelectTrigger className="w-36" data-testid="cuenta-selector">
                <SelectValue placeholder="Cuenta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consolidado">Consolidado</SelectItem>
                {cuentasList.map((cuenta) => (
                  <SelectItem key={cuenta.id} value={cuenta.id}>
                    {cuenta.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Compare Years Toggle */}
            <Button 
              variant={compareMode ? "default" : "outline"}
              size="sm"
              onClick={() => setCompareMode(!compareMode)}
              className="gap-1"
            >
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Comparar</span>
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                // Get empresa/cliente name from user context
                let empresaNombre = 'FinScope';
                if (user?.cliente_nombre) {
                  empresaNombre = user.cliente_nombre;
                } else if (user?.empresa_nombre) {
                  empresaNombre = user.empresa_nombre;
                } else if (user?.role === 'super_admin') {
                  empresaNombre = 'Todas las empresas';
                }
                
                const cuentaNombre = selectedCuenta === 'consolidado' 
                  ? 'Consolidado' 
                  : cuentasList.find(c => c.id === selectedCuenta)?.nombre || '';
                
                const mesLabel = `${monthNames.find(m => m.value === selectedMonth)?.label || ''} ${selectedYear}`;
                
                const subtitulo = selectedCuenta === 'consolidado'
                  ? `Consolidado - ${empresaNombre} | ${mesLabel}`
                  : `${cuentaNombre} - ${empresaNombre} | ${mesLabel}`;
                
                exportDashboardToPDF(kpis, tendencias, gastosPorCategoria, subtitulo);
                toast.success('PDF generado exitosamente');
              }}
              data-testid="export-pdf-btn"
              className="hidden sm:flex"
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Year Comparison Panel */}
        {compareMode && (
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Comparador de Años
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Year Selection */}
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">Selecciona los años a comparar:</p>
                <div className="flex flex-wrap gap-2">
                  {(availableYears.length > 0 ? availableYears : Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i))).map((year) => (
                    <Button
                      key={year}
                      variant={compareYears.includes(year) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleCompareYear(year)}
                      className={compareYears.includes(year) ? "bg-blue-600 hover:bg-blue-700" : ""}
                    >
                      {year}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Comparison Results */}
              {comparisonData && comparisonData.length > 0 && (
                <div className="space-y-4">
                  {/* Comparison Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 px-3 font-medium">Año</th>
                          <th className="text-right py-2 px-3 font-medium text-green-700">Ingresos</th>
                          <th className="text-right py-2 px-3 font-medium text-red-700">Egresos</th>
                          <th className="text-right py-2 px-3 font-medium text-blue-700">Utilidad</th>
                          <th className="text-right py-2 px-3 font-medium">Margen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.map((data, idx) => (
                          <tr key={data.year} className={idx % 2 === 0 ? 'bg-white/50' : ''}>
                            <td className="py-2 px-3 font-bold">{data.year}</td>
                            <td className="py-2 px-3 text-right text-green-700 font-mono">
                              {formatCurrencyCompact(data.kpis?.ingresos || 0)}
                            </td>
                            <td className="py-2 px-3 text-right text-red-700 font-mono">
                              {formatCurrencyCompact(data.kpis?.egresos || 0)}
                            </td>
                            <td className="py-2 px-3 text-right text-blue-700 font-mono font-medium">
                              {formatCurrencyCompact(data.kpis?.utilidad_bruta || 0)}
                            </td>
                            <td className="py-2 px-3 text-right font-medium">
                              {data.kpis?.margen || 0}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Comparison Chart */}
                  {comparisonData.length >= 2 && (
                    <div className="h-64 mt-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={comparisonData.map(d => ({
                            year: d.year,
                            Ingresos: d.kpis?.ingresos || 0,
                            Egresos: d.kpis?.egresos || 0,
                            Utilidad: d.kpis?.utilidad_bruta || 0
                          }))}
                          margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatCurrencyCompact(v)} />
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                          />
                          <Legend />
                          <Bar dataKey="Ingresos" fill="#10B981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Egresos" fill="#EF4444" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Utilidad" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
              
              {compareYears.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  Selecciona al menos un año para ver la comparación
                </p>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* KPIs */}
        <div className="kpi-grid">
          <KPICard
            title="Ingresos"
            value={formatCurrencyCompact(kpis?.ingresos || 0)}
            change={kpis?.cambio_ingresos}
            icon={TrendingUp}
            trend="up"
            subtitle={kpis?.mes_anterior ? `Mes ant: ${formatCurrencyCompact(kpis.mes_anterior.ingresos)}` : null}
          />
          <KPICard
            title="Egresos"
            value={formatCurrencyCompact(kpis?.egresos || 0)}
            change={kpis?.cambio_egresos}
            icon={TrendingDown}
            trend="down"
            subtitle={kpis?.mes_anterior ? `Mes ant: ${formatCurrencyCompact(kpis.mes_anterior.egresos)}` : null}
          />
          <KPICard
            title="Utilidad"
            value={formatCurrencyCompact(kpis?.utilidad_bruta || 0)}
            icon={DollarSign}
            trend={kpis?.utilidad_bruta >= 0 ? 'up' : 'down'}
          />
          <KPICard
            title="Margen"
            value={`${kpis?.margen || 0}%`}
            icon={Percent}
            trend={kpis?.margen >= 20 ? 'up' : 'down'}
          />
        </div>

        {/* Onboarding Checklist - show for new users */}
        {!user?.onboarding_completado && (
          <div className="mb-6">
            <OnboardingChecklist />
          </div>
        )}

        {/* Charts Row */}
        <div className="charts-grid">
          {/* Trend Chart */}
          <Card className="chart-container animate-slideUp stagger-1">
            <CardHeader className="flex flex-row items-center justify-between p-3 sm:pb-2">
              <CardTitle className="text-sm sm:text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  Tendencia
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 sm:p-6">
              <div className="h-48 sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={tendencias}>
                    <defs>
                      <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorEgresos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#BE123C" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#BE123C" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="mes_label" tick={{ fontSize: 10 }} stroke="#64748B" interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#64748B" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} width={45} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="ingresos"
                      stroke="#10B981"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorIngresos)"
                      name="Ingresos"
                    />
                    <Area
                      type="monotone"
                      dataKey="egresos"
                      stroke="#BE123C"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorEgresos)"
                      name="Egresos"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Gastos por Categoría */}
          <Card className="chart-container animate-slideUp stagger-2">
            <CardHeader className="flex flex-row items-center justify-between p-3 sm:pb-2">
              <CardTitle className="text-sm sm:text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  Categorías
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 sm:p-6">
              {/* Mobile: Stack chart and legend vertically */}
              <div className="block sm:hidden">
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={gastosPorCategoria}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        fill="#8884d8"
                        paddingAngle={1}
                        dataKey="total"
                        nameKey="categoria_nombre"
                        label={({ percent }) => percent > 0.03 ? `${(percent * 100).toFixed(0)}%` : ''}
                        labelLine={false}
                      >
                        {gastosPorCategoria.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [formatCurrency(value), name]}
                        labelFormatter={() => ''}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                {/* Mobile legend - scrollable if many categories */}
                <div className="mt-3 max-h-32 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 px-3">
                    {gastosPorCategoria.map((entry, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        <div 
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: entry.color || COLORS[index % COLORS.length] }}
                        />
                        <span className="text-[11px] text-gray-700 truncate">{entry.categoria_nombre}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Desktop: Side legend with better label positioning */}
              <div className="hidden sm:block h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={gastosPorCategoria}
                      cx="30%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      fill="#8884d8"
                      paddingAngle={2}
                      dataKey="total"
                      nameKey="categoria_nombre"
                      label={({ cx, cy, midAngle, outerRadius, percent }) => {
                        if (percent < 0.02) return null;
                        const RADIAN = Math.PI / 180;
                        const radius = outerRadius + 20;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill="#374151"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            fontSize={11}
                            fontWeight={500}
                          >
                            {`${(percent * 100).toFixed(0)}%`}
                          </text>
                        );
                      }}
                      labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
                    >
                      {gastosPorCategoria.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      labelFormatter={() => ''}
                    />
                    <Legend 
                      layout="vertical" 
                      align="right" 
                      verticalAlign="top"
                      wrapperStyle={{ paddingLeft: '10px', maxHeight: '280px', overflowY: 'auto' }}
                      formatter={(value, entry) => (
                        <span className="text-sm text-gray-700 font-medium">{entry.payload.categoria_nombre}</span>
                      )}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row - Hidden on mobile for simplicity */}
        <div className="charts-grid mt-4 hidden sm:grid">
          {/* Margin Trend - Redesigned */}
          <Card className="chart-container animate-slideUp stagger-3">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-primary" />
                  Margen Mensual
                </div>
              </CardTitle>
              {/* Quick Summary */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">Promedio:</span>
                <span className={`font-bold ${(tendencias.reduce((acc, t) => acc + (t.margen || 0), 0) / Math.max(tendencias.length, 1)) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {(tendencias.reduce((acc, t) => acc + (t.margen || 0), 0) / Math.max(tendencias.length, 1)).toFixed(1)}%
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={tendencias} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.05}/>
                      </linearGradient>
                      <linearGradient id="negativeGradient" x1="0" y1="1" x2="0" y2="0">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#EF4444" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis 
                      dataKey="mes_label" 
                      tick={{ fontSize: 11, fill: '#64748B' }} 
                      axisLine={{ stroke: '#E2E8F0' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#64748B' }} 
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v}%`}
                      domain={['auto', 'auto']}
                    />
                    <ReferenceLine y={0} stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 4" />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'Margen') return [`${value?.toFixed(1)}%`, 'Margen'];
                        return null;
                      }}
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="margen" 
                      radius={[4, 4, 0, 0]}
                      name="Margen"
                      maxBarSize={40}
                    >
                      {tendencias.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.margen >= 0 ? '#10B981' : '#EF4444'}
                          fillOpacity={0.85}
                        />
                      ))}
                    </Bar>
                    <Line
                      type="monotone"
                      dataKey="margen"
                      stroke="#6366F1"
                      strokeWidth={2.5}
                      dot={{ fill: '#6366F1', strokeWidth: 2, r: 4, stroke: '#fff' }}
                      activeDot={{ r: 6, fill: '#6366F1', stroke: '#fff', strokeWidth: 2 }}
                      name="tendencia"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* YTD Summary */}
          <Card className="chart-container animate-slideUp stagger-4">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary" />
                  Resumen YTD
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ingresos YTD</p>
                      <p className="text-xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatCurrency(kpis?.ytd?.ingresos || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Egresos YTD</p>
                      <p className="text-xl font-bold text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatCurrency(kpis?.ytd?.egresos || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border-2 border-primary/20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <DollarSign className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Utilidad YTD</p>
                      <p className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatCurrency(kpis?.ytd?.utilidad || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
